import { useId, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Zone de dépôt de fichiers réutilisable (Lot D 2026-06-27) — drag & drop
 * multi-fichiers + sélection au clic, design Stripe-like.
 *
 * Formats acceptés (alignés sur l'allowlist serveur `upload-validation.ts`) :
 * pdf, png, jpg/jpeg, msg, eml, docx, xlsx. La validation autoritaire (taille,
 * magic bytes, ClamAV) est faite côté serveur — `accept` n'est qu'un confort UX.
 *
 * Drag Outlook : un mail/PJ glissé depuis Outlook (bureau, Chrome/Edge) arrive
 * via `dataTransfer.files` quand le navigateur expose le fichier virtuel — le
 * dropzone le capte sans code spécifique. Sinon : enregistrer le mail (.msg/.eml)
 * puis le glisser/sélectionner.
 */

/** Extensions acceptées — miroir de l'allowlist serveur. */
export const ACCEPTED_UPLOAD_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.msg',
  '.eml',
  '.docx',
  '.xlsx',
] as const;

/** Valeur de l'attribut HTML `accept` (extensions + quelques mimes courants). */
export const UPLOAD_ACCEPT_ATTR = [
  ...ACCEPTED_UPLOAD_EXTENSIONS,
  'application/pdf',
  'image/png',
  'image/jpeg',
  'message/rfc822',
  'application/vnd.ms-outlook',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

interface DropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
  /** Texte d'aide sous le libellé (sinon valeur par défaut listant les formats). */
  hint?: string;
}

export function Dropzone({ onFiles, disabled = false, multiple = true, hint }: DropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function emit(list: FileList | null): void {
    const files = list ? Array.from(list) : [];
    if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1));
    if (inputRef.current) inputRef.current.value = ''; // permet de re-déposer le même fichier
  }

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed p-6 text-center transition-colors',
        dragOver
          ? 'border-accent bg-accent/5'
          : 'border-border-subtle bg-gray-50 hover:bg-gray-100',
        disabled && 'pointer-events-none opacity-60',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        emit(e.dataTransfer.files);
      }}
    >
      <UploadCloud className="h-7 w-7 text-text-muted" />
      <span className="text-sm font-medium text-brand">
        Glissez {multiple ? 'des fichiers' : 'un fichier'} ici ou cliquez pour sélectionner
      </span>
      <span className="text-xs text-text-muted">
        {hint ?? 'PDF, images (PNG/JPG), Word/Excel, mails Outlook (.msg/.eml) · max 25 Mo'}
      </span>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple={multiple}
        accept={UPLOAD_ACCEPT_ATTR}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => emit(e.target.files)}
      />
    </label>
  );
}
